import React, { useEffect } from 'react';
import { ArrowLeft, Shield, Database, Users, RefreshCw, Mail } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BubbleShooterPrivacy = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1a1035] via-[#2d1b4e] to-[#1a1035] relative overflow-hidden">
      {/* Decorative bubbles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-16 h-16 rounded-full bg-[#FF6B6B]/20 blur-xl"></div>
        <div className="absolute top-40 right-20 w-24 h-24 rounded-full bg-[#3B82F6]/20 blur-xl"></div>
        <div className="absolute top-60 left-1/4 w-12 h-12 rounded-full bg-[#22C55E]/20 blur-xl"></div>
        <div className="absolute bottom-40 right-1/4 w-20 h-20 rounded-full bg-[#FFE66D]/20 blur-xl"></div>
        <div className="absolute bottom-20 left-20 w-14 h-14 rounded-full bg-[#A855F7]/20 blur-xl"></div>
      </div>

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#1a1035]/90 backdrop-blur-md border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>
          <div className="flex items-center gap-3">
            <img
              src="/images/bubbleshooter-logo.png"
              alt="Bubble Shooter"
              className="w-10 h-10 rounded-xl"
            />
            <span className="font-semibold text-white">Bubble Shooter</span>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-32 pb-16 px-6 relative">
        <div className="max-w-4xl mx-auto flex flex-col items-center text-center">
          <div className="relative mb-8">
            <img
              src="/images/bubbleshooter-logo.png"
              alt="Bubble Shooter"
              className="w-28 h-28 rounded-[1.5rem] shadow-2xl shadow-purple-500/30 mx-auto"
            />
            <div className="absolute -inset-4 rounded-[2rem] bg-gradient-to-r from-[#FF6B6B] via-[#A855F7] to-[#3B82F6] opacity-30 blur-xl -z-10"></div>
          </div>

          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6 border border-white/20">
            <Shield className="w-4 h-4 text-[#22C55E]" />
            <span className="text-white/90 text-sm font-medium">Your Privacy Matters</span>
          </div>

          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-white">
            Privacy Policy
          </h1>
          <p className="text-white/70 text-lg max-w-2xl">
            Bubble Shooter is designed with your privacy in mind. We believe in transparency about how we handle your data.
          </p>
          <p className="text-white/40 text-sm mt-4">Last Updated: December 2024</p>
        </div>
      </section>

      {/* Content */}
      <section className="pb-20 px-6 relative">
        <div className="max-w-4xl mx-auto space-y-6">

          {/* Data Collection */}
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-[#3B82F6]/20 flex items-center justify-center">
                <Database className="w-6 h-6 text-[#3B82F6]" />
              </div>
              <h2 className="text-2xl font-semibold text-white">Data Collection</h2>
            </div>
            <p className="text-white/70 mb-4">
              Bubble Shooter is designed with your privacy as a priority. We collect minimal data to provide you with a great gaming experience.
            </p>
            <ul className="space-y-3 text-white/70">
              <li className="flex items-start gap-3">
                <span className="text-[#22C55E] mt-1 font-bold">→</span>
                <span>High scores are stored locally on your device only</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#22C55E] mt-1 font-bold">→</span>
                <span>Game settings (sound, music, haptics) are stored locally</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#22C55E] mt-1 font-bold">→</span>
                <span>No personal information is collected or transmitted</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#22C55E] mt-1 font-bold">→</span>
                <span>No analytics or tracking services are used</span>
              </li>
            </ul>
          </div>

          {/* Third-Party Services */}
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-[#A855F7]/20 flex items-center justify-center">
                <Users className="w-6 h-6 text-[#A855F7]" />
              </div>
              <h2 className="text-2xl font-semibold text-white">Third-Party Services</h2>
            </div>
            <p className="text-white/70 mb-4">
              Bubble Shooter does not use any third-party services that collect data:
            </p>
            <ul className="space-y-3 text-white/70">
              <li className="flex items-start gap-3">
                <span className="text-[#FF6B6B] mt-1 font-bold">✕</span>
                <span>No advertising networks</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#FF6B6B] mt-1 font-bold">✕</span>
                <span>No analytics services</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#FF6B6B] mt-1 font-bold">✕</span>
                <span>No social media integrations</span>
              </li>
            </ul>
            <p className="text-white/50 text-sm mt-4">
              The game works completely offline with no internet connection required.
            </p>
          </div>

          {/* Data Storage */}
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-[#22C55E]/20 flex items-center justify-center">
                <Shield className="w-6 h-6 text-[#22C55E]" />
              </div>
              <h2 className="text-2xl font-semibold text-white">Data Storage</h2>
            </div>
            <p className="text-white/70">
              All game data including high scores, settings preferences, and game progress is stored locally on your device using iOS UserDefaults. This data is never transmitted to external servers. You can delete all game data by uninstalling the app.
            </p>
          </div>

          {/* Children's Privacy */}
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-[#FFE66D]/20 flex items-center justify-center">
                <Users className="w-6 h-6 text-[#FFE66D]" />
              </div>
              <h2 className="text-2xl font-semibold text-white">Children's Privacy</h2>
            </div>
            <p className="text-white/70">
              Bubble Shooter is safe for all ages. Since we do not collect any personal information, the app complies with COPPA (Children's Online Privacy Protection Act) requirements. The game contains no ads, in-app purchases, or external links.
            </p>
          </div>

          {/* Changes to Policy */}
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-[#FF9F43]/20 flex items-center justify-center">
                <RefreshCw className="w-6 h-6 text-[#FF9F43]" />
              </div>
              <h2 className="text-2xl font-semibold text-white">Changes to This Policy</h2>
            </div>
            <p className="text-white/70">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.
            </p>
          </div>

          {/* Contact */}
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 text-center border border-white/10">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-r from-[#A855F7] to-[#3B82F6] flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-semibold text-white mb-3">Questions?</h2>
            <p className="text-white/70 mb-6 max-w-lg mx-auto">
              If you have any questions about this Privacy Policy, please contact us.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="mailto:tymarbeit@gmail.com"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-[#A855F7] to-[#3B82F6] hover:opacity-90 transition-opacity text-white px-6 py-3 rounded-full font-medium shadow-lg shadow-purple-500/30"
              >
                <Mail className="w-5 h-5" />
                tymarbeit@gmail.com
              </a>
              <a
                href="mailto:tymur@abkiliamov.com"
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm transition-colors text-white px-6 py-3 rounded-full font-medium border border-white/20"
              >
                <Mail className="w-5 h-5" />
                tymur@abkiliamov.com
              </a>
            </div>
          </div>

        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 px-6 text-center">
        <p className="text-white/40 text-sm">
          &copy; 2024 Bubble Shooter. All rights reserved.
        </p>
        <div className="flex items-center justify-center gap-4 mt-3">
          <a
            href="https://github.com/deeplearnproger/BubbleShooter"
            target="_blank"
            rel="noopener noreferrer"
            className="text-white/40 hover:text-white transition-colors text-sm"
          >
            GitHub
          </a>
          <span className="text-white/20">·</span>
          <button
            onClick={() => navigate('/bubbleshooter')}
            className="text-white/40 hover:text-white transition-colors text-sm"
          >
            About
          </button>
          <span className="text-white/20">·</span>
          <button
            onClick={() => navigate('/')}
            className="text-white/40 hover:text-white transition-colors text-sm"
          >
            Portfolio
          </button>
        </div>
      </footer>
    </div>
  );
};

export default BubbleShooterPrivacy;
